import { useCallback, useRef } from 'react'

/**
 * Draggable trim handle for the slicer. Renders one of:
 *
 *   - position='start': yellow/dashed line at `trimSec`, anchored
 *     to the LEFT edge of the playable region. Range is
 *     `[0, neighbourSec - MIN_GAP]`.
 *   - position='end':   same visual flipped horizontally, anchored
 *     to the RIGHT edge. Range is `[neighbourSec + MIN_GAP,
 *     durationSec]`.
 *
 * Distinct from `SliceMarkers` (which renders interior slice
 * boundaries) for two reasons:
 *
 *   1. Visual: trim is yellow/dashed over a shaded "muted" region
 *      so the user reads it as "everything outside this is hidden"
 *      rather than as a regular slice boundary.
 *   2. Semantic: trim's clamp range is bound by the *outer* slice
 *      neighbour (first slice's end for start; last slice's start
 *      for end), not by adjacent slice boundaries the way
 *      `SliceMarkers` clamps.
 *
 * Muted shading itself is drawn by `WaveformViewer` (it owns the
 * canvas); this component renders only the draggable handle so
 * pointer events land on it without forcing a canvas redraw on
 * every drag tick.
 */

const MIN_GAP_SEC = 0.01

interface TrimMarkerProps {
  position: 'start' | 'end'
  /** The current trim value in seconds. For 'start' this is
   *  measured from t=0; for 'end' it's also measured from t=0
   *  (i.e. the absolute file time, not the offset from the end). */
  trimSec: number
  durationSec: number
  /** Clamp neighbour. For 'start': end of slice 1 (the first slice
   *  has to retain at least MIN_GAP_SEC of length). For 'end':
   *  start of the last slice (similarly). */
  neighbourSec: number
  width: number
  height: number
  /** Snap resolution in seconds (e.g. secPer16th). Undefined = free. */
  snapSec?: number
  onChange: (next: number) => void
}

export function TrimMarker({
  position,
  trimSec,
  durationSec,
  neighbourSec,
  width,
  height,
  snapSec,
  onChange,
}: TrimMarkerProps) {
  const dragRef = useRef<{ pointerId: number } | null>(null)
  const pxPerSec = durationSec > 0 ? width / durationSec : 0

  const clamp = useCallback(
    (desired: number): number => {
      const lo =
        position === 'start'
          ? 0
          : Math.min(durationSec, neighbourSec + MIN_GAP_SEC)
      const hi =
        position === 'start'
          ? Math.max(0, neighbourSec - MIN_GAP_SEC)
          : durationSec
      let v = Math.max(lo, Math.min(hi, desired))
      if (snapSec && snapSec > 0) {
        const snapped = Math.round(v / snapSec) * snapSec
        if (snapped >= lo && snapped <= hi) v = snapped
      }
      return v
    },
    [position, durationSec, neighbourSec, snapSec],
  )

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    ;(e.target as Element).setPointerCapture(e.pointerId)
    dragRef.current = { pointerId: e.pointerId }
    e.preventDefault()
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const rect = e.currentTarget.parentElement?.getBoundingClientRect()
    if (!rect) return
    const relX = e.clientX - rect.left
    const desired = pxPerSec > 0 ? relX / pxPerSec : 0
    onChange(clamp(desired))
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (drag && drag.pointerId === e.pointerId) {
      dragRef.current = null
    }
  }

  // Hide entirely when there's no audio yet (durationSec === 0).
  if (pxPerSec <= 0) return null

  const left = trimSec * pxPerSec
  const tooltip =
    position === 'start'
      ? `Trim start: ${trimSec.toFixed(3)}s — drag to crop leading silence`
      : `Trim end: ${trimSec.toFixed(3)}s — drag to crop tail silence`

  // Triangle direction: start triangle points DOWN-RIGHT (into the
  // playable region); end triangle points DOWN-LEFT. Both sit at
  // the top so the boundary line extends from the triangle tip.
  const triangleClip =
    position === 'start'
      ? 'polygon(0 0, 100% 0, 50% 100%)'
      : 'polygon(0 0, 100% 0, 50% 100%)'

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      title={tooltip}
      style={{
        position: 'absolute',
        left: left - 6,
        top: 0,
        width: 12,
        height,
        cursor: 'ew-resize',
        touchAction: 'none',
      }}
    >
      {/* Dashed yellow line — visually distinct from the pink
          interior slice boundaries. Same for both start and end. */}
      <div
        style={{
          position: 'absolute',
          left: 5,
          top: 0,
          width: 2,
          height,
          background:
            'repeating-linear-gradient(180deg, rgba(250,204,21,0.95) 0 6px, transparent 6px 10px)',
          pointerEvents: 'none',
        }}
      />
      {/* Triangle handle on top so the user knows it's grabbable. */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 12,
          height: 12,
          background: 'rgba(250,204,21,0.9)',
          clipPath: triangleClip,
          pointerEvents: 'none',
        }}
      />
    </div>
  )
}
